import http, { request } from './http';

export interface Result<T> {
  success: boolean;
  message?: string;
  data?: T;
}

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const res = (await promise) as Result<T>;
  if (!res?.success) {
    throw new Error(res?.message ?? '请求失败');
  }
  return res.data as T;
}

export interface OrganizationSyncProgress {
  status: 'idle' | 'running' | 'success' | 'failed';
  progress?: number;
  message?: string;
  phase?: string;
  platform?: string;
  totalTenants?: number;
  completedTenants?: number;
  deptSuccessCount?: number;
  deptFailureCount?: number;
  userSuccessCount?: number;
  userFailureCount?: number;
  tenantFailureCount?: number;
  lastSuccessAt?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}

export async function syncOrg(): Promise<void> {
  await unwrap(request(http.post('/admin/org/sync-org')));
}

export async function getOrgSyncStatus(): Promise<OrganizationSyncProgress> {
  return unwrap(request(http.get('/admin/org/sync-org/status')));
}

export async function getFormalDeptTree(): Promise<any[]> {
  return unwrap(request(http.get('/admin/org/members/formal-tree')));
}

export async function getFormalDeptUsers(
  deptId: number | string,
  params?: { keyword?: string; page?: number; size?: number },
): Promise<any[]> {
  const page = await getFormalDeptUsersPage(deptId, params);
  return page.records ?? [];
}

export async function getFormalDeptUsersPage(
  deptId?: number | string | null,
  params?: { keyword?: string; page?: number; size?: number },
): Promise<{ records: any[]; total: number; page: number; size: number }> {
  const query = {
    keyword: params?.keyword,
    page: params?.page ?? 1,
    size: params?.size ?? 100,
  };
  if (deptId == null || deptId === '') {
    return unwrap(request(http.get('/admin/org/members/formal/users', { params: query })));
  }
  return unwrap(
    request(http.get(`/admin/org/members/formal/${deptId}/users`, { params: query })),
  );
}

export async function patchOrgUser(
  userId: number,
  body: { nickname?: string; status?: string; username?: string },
): Promise<any> {
  const res = (await request(http.patch(`/admin/org/members/users/${userId}`, body))) as Result<any>;
  if (!res?.success) {
    const err = new Error(res?.message ?? '请求失败') as Error & { data?: any };
    err.data = res?.data;
    throw err;
  }
  return res.data;
}

export async function suggestOrgUsername(params?: {
  realName?: string;
  preferred?: string;
  excludeUserId?: number;
}): Promise<{ username: string }> {
  return unwrap(
    request(http.get('/admin/org/members/users/suggest-username', { params })),
  );
}

export async function resetOrgUserPassword(
  userId: number,
  password?: string,
): Promise<{ temporaryPassword: string; password?: string }> {
  return unwrap(
    request(http.post(`/admin/org/members/users/${userId}/reset-password`, password ? { password } : {})),
  );
}

export async function getVirtualDeptTree(): Promise<any[]> {
  return unwrap(request(http.get('/admin/org/virtual-departments/tree')));
}

export async function createVirtualDept(body: {
  name: string;
  parentId?: number | null;
}): Promise<any> {
  return unwrap(request(http.post('/admin/org/virtual-departments', body)));
}

export async function updateVirtualDept(id: number, body: { name: string }): Promise<any> {
  return unwrap(request(http.put(`/admin/org/virtual-departments/${id}`, body)));
}

export async function deleteVirtualDept(id: number): Promise<void> {
  await unwrap(request(http.delete(`/admin/org/virtual-departments/${id}`)));
}

export async function getVirtualDeptMembers(id: number): Promise<any[]> {
  return unwrap(request(http.get(`/admin/org/virtual-departments/${id}/members`)));
}

export async function syncVirtualDeptMembers(id: number, userIds: number[]): Promise<any[]> {
  return unwrap(
    request(http.put(`/admin/org/virtual-departments/${id}/members`, { userIds })),
  );
}

export async function addVirtualDeptMembers(id: number, userIds: number[]): Promise<void> {
  for (const userId of userIds) {
    await unwrap(request(http.post(`/admin/org/virtual-departments/${id}/members`, { userId })));
  }
}

export async function removeVirtualDeptMember(id: number, userId: number): Promise<void> {
  await unwrap(request(http.delete(`/admin/org/virtual-departments/${id}/members/${userId}`)));
}

/** 临时成员 */
export async function listTempMembers(keyword?: string): Promise<any[]> {
  return unwrap(
    request(http.get('/admin/org/temp-members', { params: keyword ? { keyword } : {} })),
  );
}

export async function countTempMembers(): Promise<number> {
  const res = await unwrap<{ count: number }>(request(http.get('/admin/org/temp-members/count')));
  return res?.count ?? 0;
}

export async function createTempMember(body: Record<string, unknown>): Promise<any> {
  const res = (await request(http.post('/admin/org/temp-members', body))) as Result<any>;
  if (!res?.success) {
    const err = new Error(res?.message ?? '请求失败') as Error & { data?: any };
    err.data = res?.data;
    throw err;
  }
  return res.data;
}

export async function updateTempMember(id: number, body: Record<string, unknown>): Promise<any> {
  const res = (await request(http.put(`/admin/org/temp-members/${id}`, body))) as Result<any>;
  if (!res?.success) {
    const err = new Error(res?.message ?? '请求失败') as Error & { data?: any };
    err.data = res?.data;
    throw err;
  }
  return res.data;
}

export async function resetTempMemberPassword(
  id: number,
  password?: string,
): Promise<{ temporaryPassword: string; password?: string }> {
  return unwrap(
    request(
      http.post(`/admin/org/temp-members/${id}/reset-password`, password ? { password } : {}),
    ),
  );
}

export async function setTempMemberStatus(id: number, status: string): Promise<any> {
  return unwrap(request(http.patch(`/admin/org/temp-members/${id}/status`, { status })));
}

export async function listVirtualUsers(): Promise<any[]> {
  return unwrap(request(http.get('/admin/org/virtual-users')));
}

export async function createVirtualUser(body: {
  username?: string;
  displayName: string;
  password: string;
  virtualDepartmentId?: number | null;
  roleCode?: string | null;
  expiresAt?: string | null;
}): Promise<any> {
  const res = (await request(http.post('/admin/org/virtual-users', body))) as Result<any>;
  if (!res?.success) {
    const err = new Error(res?.message ?? '请求失败') as Error & { data?: any };
    err.data = res?.data;
    throw err;
  }
  return res.data;
}

export async function updateVirtualUser(
  id: number,
  body: {
    displayName?: string;
    realName?: string;
    username?: string;
    password?: string;
    status?: string;
    expiresAt?: string | null;
    roleCode?: string | null;
  },
): Promise<any> {
  const res = (await request(http.put(`/admin/org/virtual-users/${id}`, body))) as Result<any>;
  if (!res?.success) {
    const err = new Error(res?.message ?? '请求失败') as Error & { data?: any };
    err.data = res?.data;
    throw err;
  }
  return res.data;
}

export async function resetVirtualUserPassword(
  id: number,
  password?: string,
): Promise<{ temporaryPassword: string; password?: string }> {
  return unwrap(
    request(http.post(`/admin/org/virtual-users/${id}/reset-password`, password ? { password } : {})),
  );
}

export async function deleteVirtualUser(id: number): Promise<void> {
  await unwrap(request(http.delete(`/admin/org/virtual-users/${id}`)));
}

export async function listSuperAdmins(): Promise<any[]> {
  return unwrap(request(http.get('/admin/org/super-admins')));
}

/** 移交唯一超级管理员；需当前超管密码；默认同时删除引导账号 admin */
export async function transferSuperAdmin(
  userId: number | string,
  deleteBootstrapAdmin = true,
  password?: string,
): Promise<any> {
  return unwrap(
    request(
      http.post('/admin/org/super-admins/transfer', {
        userId,
        deleteBootstrapAdmin,
        password,
      }),
    ),
  );
}

export async function listSubAdmins(): Promise<any[]> {
  return unwrap(request(http.get('/admin/org/sub-admins')));
}

export async function addSubAdmin(userId: number | string): Promise<void> {
  await unwrap(request(http.post('/admin/org/sub-admins', { userId })));
}

export async function removeSubAdmin(userId: number): Promise<void> {
  await unwrap(request(http.delete(`/admin/org/sub-admins/${userId}`)));
}

/** Roles */
export async function listAdminRoles(): Promise<any[]> {
  return unwrap(request(http.get('/admin/roles')));
}

export async function createAdminRole(body: {
  code?: string;
  name: string;
  description?: string;
}): Promise<any> {
  return unwrap(request(http.post('/admin/roles', body)));
}

export async function copyAdminRole(id: number): Promise<any> {
  return unwrap(request(http.post(`/admin/roles/${id}/copy`)));
}

export async function updateAdminRole(
  id: number,
  body: { name?: string; description?: string },
): Promise<any> {
  return unwrap(request(http.put(`/admin/roles/${id}`, body)));
}

export async function deleteAdminRole(id: number): Promise<void> {
  await unwrap(request(http.delete(`/admin/roles/${id}`)));
}

export async function getRoleFunctionPermissions(id: number): Promise<string[]> {
  return unwrap(request(http.get(`/admin/roles/${id}/permissions`)));
}

export async function putRoleFunctionPermissions(id: number, permissionCodes: string[]): Promise<void> {
  await unwrap(request(http.put(`/admin/roles/${id}/permissions`, { permissionCodes })));
}

export async function getRoleUsers(id: number): Promise<any[]> {
  return unwrap(request(http.get(`/admin/roles/${id}/users`)));
}

export async function addRoleUsers(id: number, userIds: number[]): Promise<void> {
  await unwrap(request(http.post(`/admin/roles/${id}/users`, { userIds })));
}

export async function removeRoleUser(id: number, userId: number): Promise<void> {
  await unwrap(request(http.delete(`/admin/roles/${id}/users/${userId}`)));
}

export async function removeRoleUsers(id: number, userIds: number[]): Promise<void> {
  await unwrap(request(http.post(`/admin/roles/${id}/users/batch-remove`, { userIds })));
}

export async function getRoleOrganizations(id: number): Promise<any[]> {
  return unwrap(request(http.get(`/admin/roles/${id}/organizations`)));
}

export async function addRoleOrganization(
  id: number,
  body: { orgKind: 'FORMAL' | 'VIRTUAL'; orgId: number } | { organizations: Array<{ orgKind: 'FORMAL' | 'VIRTUAL'; orgId: number }> },
): Promise<void> {
  await unwrap(request(http.post(`/admin/roles/${id}/organizations`, body)));
}

export async function searchAssignableUsers(params?: {
  keyword?: string;
  limit?: number;
  excludeRoleId?: number;
  memberType?: 'FORMAL' | 'VIRTUAL' | 'TEMP' | 'ALL';
}): Promise<any[]> {
  return unwrap(
    request(http.get('/admin/roles/candidates/users', {
      params: {
        keyword: params?.keyword,
        limit: params?.limit ?? 50,
        excludeRoleId: params?.excludeRoleId,
        memberType: params?.memberType,
      },
    })),
  );
}

export async function removeRoleOrganization(
  id: number,
  orgKind: string,
  orgId: number,
): Promise<void> {
  await unwrap(
    request(http.delete(`/admin/roles/${id}/organizations`, { params: { orgKind, orgId } })),
  );
}

export async function getRoleFieldPermissions(id: number): Promise<Record<string, string>> {
  return unwrap(request(http.get(`/admin/roles/${id}/field-permissions`)));
}

export async function putRoleFieldPermissions(
  id: number,
  map: Record<string, string>,
): Promise<void> {
  await unwrap(request(http.put(`/admin/roles/${id}/field-permissions`, map)));
}

export async function getRoleDataPermissions(id: number): Promise<Record<string, string>> {
  return unwrap(request(http.get(`/admin/roles/${id}/data-permissions`)));
}

export async function putRoleDataPermissions(
  id: number,
  map: Record<string, string>,
): Promise<void> {
  await unwrap(request(http.put(`/admin/roles/${id}/data-permissions`, map)));
}

export async function getFieldCatalog(): Promise<any[]> {
  return unwrap(request(http.get('/admin/roles/catalog/fields')));
}

export async function getDataResourceCatalog(): Promise<any[]> {
  return unwrap(request(http.get('/admin/roles/catalog/data-resources')));
}

export async function getFunctionPermissionCatalog(): Promise<{
  modules: Array<{
    id: string;
    label: string;
    groups: Array<{
      id: string;
      label: string;
      items: Array<{ code: string; id: string; label: string; grants: string }>;
    }>;
  }>;
  roleOrder?: string[];
  roleLeafCounts?: Record<string, number>;
  readonlyLeafIds?: string[];
}> {
  return unwrap(request(http.get('/admin/roles/catalog/functions')));
}

export async function listAllPermissions(): Promise<any[]> {
  const raw = await request(http.get('/permissions'));
  return Array.isArray(raw) ? raw : ((raw as Result<any[]>)?.data ?? []);
}
